import './styles.scss';

import React, { useState } from 'react';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import notification from 'antd/lib/notification';
import Select from 'antd/lib/select';
import Spin from 'antd/lib/spin';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { closeJob } from 'actions/annotation-actions';
import { t } from 'utils/i18n';

import AlignReview from './align-review';
import LibraryLine from './library-line';
import StepDefaultsModal from './step-defaults-modal';
import StepRow from './step-row';
import { restoreAligned } from './steps-api';
import { useStepsContext } from './steps-context';
import {
    blockedBy, failedFrames, localizeStepsMessage, rowFrames, runnableRemaining, scopeStep, segments, storedTarget,
    storeTarget, summary, Target, targetFrames, targetLabel,
} from './steps-model';

export default function StepsPanel(): JSX.Element | null {
    const steps = useStepsContext();
    const taskId = steps?.taskId ?? 0;
    const scope = steps?.scope ?? 'drawings';
    const noun = steps?.noun ?? 'drawing';
    const [target, setTarget] = useState<Target>(() => storedTarget(taskId, scope));
    const [defaultsOpen, setDefaultsOpen] = useState(false);
    const [alignOpen, setAlignOpen] = useState(false);
    const dispatch = useDispatch();
    const history = useHistory();
    if (!steps) return null;

    const {
        state, error, notice, busy, selected, currentFrame, resultsPending,
    } = steps;
    if (!state) {
        return (
            <div className='cvat-steps-panel'>
                {error ? <Alert type='error' showIcon message={error} /> : <Spin />}
            </div>
        );
    }

    // the frames have other images and sizes: the job is fetched again, at the same frame, with nothing
    // loaded before (a reload of the page would start from the first frame)
    const reloadJob = (): void => {
        history.replace(`${history.location.pathname}?frame=${currentFrame}`);
        dispatch(closeJob());
    };

    // a cleared selection falls back to all drawings rather than running on nothing
    const effective: Target = target === 'selected' && selected.length === 0 ? 'all' : target;
    const frames = targetFrames(effective, currentFrame, selected);
    const targetList = frames ?? state.frames;
    // every count, status and failure is of the chosen drawings only
    const scoped = state.steps.map((step) => scopeStep(step, targetList));
    const single = effective === 'current';
    const { done, total } = summary(scoped);
    const remaining = runnableRemaining(scoped, targetList);
    const restore = (): void => {
        Modal.confirm({
            title: t('Restore the original drawings?'),
            content: t('The drawings and their annotations go back to how they were before aligning.'),
            okText: t('Restore'),
            cancelText: t('Cancel'),
            onOk: async () => {
                try {
                    await steps.saveChanges();
                    await restoreAligned(taskId);
                    reloadJob();
                } catch (e) {
                    notification.error({
                        message: t('Could not restore the drawings'),
                        description: t(e instanceof Error ? e.message : String(e)),
                    });
                }
            },
        });
    };
    const selectHint = noun === 'photo' ? t('on selected (Ctrl+click photos)') : t('on selected (Ctrl+click drawings)');

    return (
        <div className='cvat-steps-panel'>
            <div className='cvat-steps-summary'>
                <div className='cvat-steps-summary-title'>
                    <strong>{t('{done} of {total} done', { done, total })}</strong>
                </div>
                <div className='cvat-steps-segments' aria-hidden>
                    {segments(scoped).map((segment, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <span key={index} className={`cvat-steps-segment cvat-steps-segment-${segment}`} />
                    ))}
                </div>
            </div>
            <div className='cvat-steps-run'>
                <Button
                    type='primary'
                    className='cvat-steps-run-remaining'
                    disabled={busy || state.running || remaining.length === 0}
                    onClick={() => steps.run({ steps: remaining, frames })}
                >
                    {t('Run remaining')}
                </Button>
                <Select
                    className='cvat-steps-target'
                    value={effective}
                    onChange={(value: Target) => {
                        setTarget(value);
                        storeTarget(taskId, value, scope);
                    }}
                    dropdownMatchSelectWidth={false}
                    virtual={false}
                    aria-label={noun === 'photo' ? t('Photos to run on') : t('Drawings to run on')}
                >
                    <Select.Option value='all'>{targetLabel('all', state.frames.length, 0, noun)}</Select.Option>
                    <Select.Option value='current'>{targetLabel('current', 0, 0, noun)}</Select.Option>
                    <Select.Option value='selected' disabled={!selected.length}>
                        {selected.length ? targetLabel('selected', 0, selected.length, noun) : selectHint}
                    </Select.Option>
                </Select>
            </div>
            {error && <Alert type='error' showIcon message={localizeStepsMessage(error)} className='cvat-steps-error' />}
            {notice && <Alert type='info' showIcon message={notice} className='cvat-steps-notice' />}
            {resultsPending && (
                <Alert
                    type='info'
                    message={t('New results are ready')}
                    action={<Button size='small' onClick={() => steps.loadResults()}>{t('Save and load')}</Button>}
                />
            )}
            <ul className='cvat-steps-list' aria-label={t('Processing steps')}>
                {scoped.map((step, index) => (
                    <StepRow
                        key={step.id}
                        step={step}
                        noun={noun}
                        single={single}
                        runningElsewhere={['running', 'queued'].includes(state.steps[index].status)}
                        busy={busy || state.running}
                        blocked={blockedBy(step, state.steps, targetList, [step.id])}
                        onRun={(force) => steps.run({ steps: [step.id], frames: rowFrames(step, frames), force })}
                        onRetryFailed={() => steps.run({ steps: [step.id], frames: failedFrames(step) })}
                        onStop={() => steps.stop()}
                        onChooseFunction={(functionId) => steps.setFunction(step.id, functionId)}
                        onToggle={(enabled) => steps.setEnabled(step.id, enabled)}
                        onAlign={() => setAlignOpen(true)}
                        onRestore={state.restorable ? restore : undefined}
                    >
                        {step.kind === 'library' && step.available && (
                            <LibraryLine
                                libraries={state.libraries ?? []}
                                fallback={scope === 'references' ? t('all of the share') : t('the references only')}
                                busy={busy || state.running}
                                taskId={taskId}
                                onChange={(libraries) => steps.setLibraries(libraries)}
                            />
                        )}
                    </StepRow>
                ))}
            </ul>
            {steps.projectId !== null && scope === 'drawings' && (
                <Button type='link' className='cvat-steps-defaults-link' onClick={() => setDefaultsOpen(true)}>
                    {t('Models and defaults')}
                </Button>
            )}
            {alignOpen && (
                <AlignReview
                    taskId={taskId}
                    onClose={(replaced) => {
                        setAlignOpen(false);
                        if (replaced) reloadJob();
                    }}
                    saveChanges={steps.saveChanges}
                />
            )}
            {defaultsOpen && steps.projectId !== null && (
                <StepDefaultsModal
                    projectId={steps.projectId}
                    onClose={(saved) => {
                        setDefaultsOpen(false);
                        if (saved) steps.refresh();
                    }}
                />
            )}
        </div>
    );
}
